import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ClaimRelationshipsForContactMethodRequest,
  PaymentTermDto,
  RelationshipDto,
  RelationshipIdsByContactInfoDto,
} from 'app/api/apiClient/generated/types';
import { useGetQuickPayableInvoices } from 'app/api/hooks/core/v1/payee/invoicesAndCreditNotes';
import {
  useClaimRelationshipsForContactMethod, useClaimRelationshipWithoutVerification,
  useGetAllRelationshipData,
  useGetRelationships,
  useVerifyPayeeContactMethod,
} from 'app/api/hooks/core/v1/payee/payeeRelationships';
import i18n from 'app/services/i18n';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import {
  useClaimRelationshipDispatch,
  useClaimRelationshipState,
} from 'app/state/ClaimRelationshipContext/hooks';
import { ClaimRelationshipDispatch } from 'app/state/ClaimRelationshipContext/Provider';
import { sortByMany } from 'app/utils/lib/array';

import { ContactInfoItem } from './RelationshipsList/BrokerList/BrokerListItemContent';
import {
  clearRelationshipIndexData,
  clearRelationshipShowData,
} from './helpers';

type DialogType = 'claimMoreBrokers' | 'quickPay' | 'verificationCode';

export const useGetSelectedContactInfo =
  (): RelationshipIdsByContactInfoDto | undefined => {
    const claimRelationshipState = useClaimRelationshipState();

    const {
      relationshipsByContactInfo,
    } = useGetAllRelationshipData();

    return useMemo(() => {
      return relationshipsByContactInfo?.find(contactInfo =>
        contactInfo.id === claimRelationshipState.verifyPayeeContactMethodPayload.payeeContactMethodId);
    },
    [relationshipsByContactInfo, claimRelationshipState.verifyPayeeContactMethodPayload.payeeContactMethodId]);
  };

export const useGetAdditionalRelationshipsForVerificationMethod = (): RelationshipDto[] => {
  const {
    unclaimedRelationships,
  } = useGetAllRelationshipData();

  const claimRelationshipState = useClaimRelationshipState();

  const selectedContactInfo = useGetSelectedContactInfo();

  return useMemo(() => {
    const potentialRelationshipIds = (selectedContactInfo?.payeeRelationshipIds ?? [])
      .filter(relationshipId => !claimRelationshipState.selectedPayeeRelationshipIds?.includes(relationshipId));

    const sortedRelationships = unclaimedRelationships?.slice().sort((relationshipA, relationshipB) => {
      return (relationshipA.payor.name ?? '').toLowerCase() > (relationshipB.payor.name ?? '').toLowerCase() ? 1 : -1;
    }) ?? [];

    return sortedRelationships.filter(relationship =>
      potentialRelationshipIds.includes(relationship.id));
  },
  [
    claimRelationshipState.selectedPayeeRelationshipIds,
    selectedContactInfo,
    unclaimedRelationships,
  ]);
};

export const useGetContactInfoByRelationshipId = (): Record<string, ContactInfoItem[]> => {
  const {
    relationshipsByContactInfo,
  } = useGetAllRelationshipData();

  return useMemo<Record<string, ContactInfoItem[]>>(() => {
    const results: Record<string, ContactInfoItem[]> = {};

    relationshipsByContactInfo?.forEach(contactInfo => {
      contactInfo.payeeRelationshipIds.forEach(payeeRelationshipId => {
        results[payeeRelationshipId] ||= [];

        const contactInfoItem = { ...contactInfo };

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete contactInfoItem.payeeRelationshipIds;

        results[payeeRelationshipId]?.push(contactInfoItem);
      });
    });

    return results;
  },
  [relationshipsByContactInfo]);
};

export const useGetSortedRelationships = (payeeRelationshipIds?: string[]): RelationshipDto[] => {
  const {
    claimedRelationships,
    unclaimedRelationships,
  } = useGetAllRelationshipData();

  const contactInfoByRelationshipId = useGetContactInfoByRelationshipId();

  // Keep the current sort order, group by unclaimed and claimed
  return useMemo(() => {
    const allRelationships: RelationshipDto[] = [
      ...claimedRelationships,
      ...unclaimedRelationships,
    ];

    return sortByMany(allRelationships,
      [
        {
          ascending: true,
          selector: relationship => {
            return Number(relationship.isClaimed);
          },
        },
        {
          ascending: false,
          selector: relationship => {
            const contactInfoItems = contactInfoByRelationshipId[relationship.id];
            const hasPreVerifiedContactInfoItem = contactInfoItems?.some(contactInfo =>
              contactInfo.isPreVerified) ?? false;

            return Number(relationship.preVerified || hasPreVerifiedContactInfoItem);
          },
        },
      ])
      .filter(relationship =>
        !payeeRelationshipIds || payeeRelationshipIds.includes(relationship.id));
  },
  [
    claimedRelationships,
    contactInfoByRelationshipId,
    payeeRelationshipIds,
    unclaimedRelationships,
  ]);
};

export const useShouldHideVerificationCodeField = (): boolean => {
  const claimRelationshipState = useClaimRelationshipState();

  const selectedContactInfo = useGetSelectedContactInfo();

  return !claimRelationshipState.verifyPayeeContactMethodPayload.payeeContactMethodId
    || !!selectedContactInfo?.isPreVerified;
};

export const useHandleSendCode = () => {
  const claimRelationshipDispatch = useClaimRelationshipDispatch();
  const claimRelationshipState = useClaimRelationshipState();

  const {
    isLoading: isSendCodeLoading,
    mutate: sendCode,
  } = useVerifyPayeeContactMethod();

  useEffect(() => {
    claimRelationshipDispatch({
      payload: {
        isRequestingCode: isSendCodeLoading,
      },
      type: 'setIsRequestingCode',
    });
  },
  [claimRelationshipDispatch, isSendCodeLoading]);

  const handleOpenVerificationCodeDialog = useCallback(() => {
    claimRelationshipDispatch({
      payload: {
        isVerificationDialogOpen: true,
      },
      type: 'setIsVerificationDialogOpen',
    });
  },
  [claimRelationshipDispatch]);

  return useCallback(() => {
    sendCode(claimRelationshipState.verifyPayeeContactMethodPayload,
      {
        onSuccess: () => {
          handleOpenVerificationCodeDialog();
        },
      });
  },
  [claimRelationshipState.verifyPayeeContactMethodPayload, handleOpenVerificationCodeDialog, sendCode]);
};

const filterQuickPayTerms = (paymentTerm: PaymentTermDto) => paymentTerm.isQuickPay;

const filterRelationshipsOfferingQuickPay = (unclaimedRelationship: RelationshipDto) =>
  !unclaimedRelationship.assignedFactor && unclaimedRelationship.availablePaymentTerms.some(filterQuickPayTerms);

export const useFilteredUnclaimedRelationships = (): RelationshipDto[] | undefined => {
  const claimRelationshipState = useClaimRelationshipState();

  const { unclaimedRelationships } = useGetAllRelationshipData();

  const selectedContactInfo = useGetSelectedContactInfo();

  const relationshipIdsForContactMethod = selectedContactInfo?.payeeRelationshipIds;

  return useMemo(() => {
    if (!claimRelationshipState.selectedPayeeRelationshipIds?.length
      && !claimRelationshipState.claimMoreBrokersPayeeRelationshipIds.length) {
      return unclaimedRelationships.filter(unclaimedRelationship =>
        (relationshipIdsForContactMethod ?? []).includes(unclaimedRelationship.id));
    }

    return unclaimedRelationships.filter(unclaimedRelationship =>
      !!claimRelationshipState.selectedPayeeRelationshipIds?.includes(unclaimedRelationship.id) ||
      claimRelationshipState.claimMoreBrokersPayeeRelationshipIds.includes(unclaimedRelationship.id));
  },
  [
    claimRelationshipState.claimMoreBrokersPayeeRelationshipIds,
    claimRelationshipState.selectedPayeeRelationshipIds,
    relationshipIdsForContactMethod,
    unclaimedRelationships,
  ]);
};

const goToNextStep = ({
  claimRelationshipDispatch,
  queryClient,
  relationshipIdsBeingClaimed,
  shouldShowClaimMoreBrokersDialog,
  shouldShowQuickPayDialog,
}: {
  claimRelationshipDispatch: ClaimRelationshipDispatch;
  queryClient: QueryClient;
  relationshipIdsBeingClaimed: string[];
  shouldShowClaimMoreBrokersDialog: boolean;
  shouldShowQuickPayDialog: boolean;
}) => {
  claimRelationshipDispatch({
    payload: {
      isClaimMoreBrokersDialogOpen: shouldShowClaimMoreBrokersDialog,
    },
    type: 'setIsClaimMoreBrokersDialogOpen',
  });

  claimRelationshipDispatch({
    payload: {
      quickPayDialogProps: {
        isOpen: shouldShowQuickPayDialog,
        payeeRelationshipIds: shouldShowQuickPayDialog ? relationshipIdsBeingClaimed : [],
      },
    },
    type: 'setQuickPayDialogProps',
  });

  if (!shouldShowClaimMoreBrokersDialog && !shouldShowQuickPayDialog) {
    claimRelationshipDispatch({
      type: 'reset',
    });

    clearRelationshipIndexData({ queryClient });
  }
};

const onSubmitVerificationCodeSuccess = async ({
  claimRelationshipDispatch,
  queryClient,
  relationshipIdsBeingClaimed,
  shouldShowClaimMoreBrokersDialog,
  shouldShowQuickPayDialog,
}: {
  claimRelationshipDispatch: ClaimRelationshipDispatch;
  queryClient: QueryClient;
  relationshipIdsBeingClaimed: string[];
  shouldShowClaimMoreBrokersDialog: boolean;
  shouldShowQuickPayDialog: boolean;
}) => {
  goToNextStep({
    claimRelationshipDispatch,
    queryClient,
    relationshipIdsBeingClaimed,
    shouldShowClaimMoreBrokersDialog,
    shouldShowQuickPayDialog,
  });

  // Run goToNextStep before closing the dialog
  // This prevents the potential effect of a dialog closing and then another re-opening
  claimRelationshipDispatch({
    payload: {
      isVerificationDialogOpen: false,
    },
    type: 'setIsVerificationDialogOpen',
  });

  claimRelationshipDispatch({
    payload: {
      isVerifyingWithInvoiceDetails: false,
    },
    type: 'setIsVerifyingWithInvoiceDetails',
  });

  await clearRelationshipShowData({
    queryClient,
    relationshipIdsBeingClaimed,
  });
};

export const useConnectToBrokersSuccessNotification = () => {
  const alertsDispatch = useAlertsDispatch();

  return useCallback((relationshipCount: number) => {
    alertsDispatch({
      payload: {
        alert: {
          alertLevel: 'success',
          message: i18n.t('payee:unclaimedRelationshipHooks.connectToBrokersSuccessNotification',
            {
              count: relationshipCount,
              defaultValue: 'You have successfully connected to {{count}} payors.',
            }),
          title: i18n.t('payee:unclaimedRelationshipHooks.connectToBrokersSuccessNotificationTitle',
            {
              count: relationshipCount,
              defaultValue: '{{count}} new connections',
            }),
        },
      },
      type: 'addAlert',
    });
  },
  [alertsDispatch]);
};

export const useSubmitVerificationCode = () => {
  // Control the submitting state ourselves.
  // We need it to remain true until the onSuccess is done, after all promises have resolved.
  // We want the submit button of the claim more brokers dialog to remain in a loading state
  const [isVerifying, setIsVerifying] = useState(false);

  const claimRelationshipDispatch = useClaimRelationshipDispatch();
  const claimRelationshipState = useClaimRelationshipState();

  const filteredUnclaimedRelationships = useFilteredUnclaimedRelationships();

  const {
    mutate: claimRelationshipsForContactMethod,
  } = useClaimRelationshipsForContactMethod();

  const {
    refetch: refetchRelationships,
  } = useGetRelationships();

  const {
    refetch: refetchQuickPayableRelationships,
  } = useGetQuickPayableInvoices({ shouldIncludeRelationshipsLackingInvoices: true });

  const queryClient = useQueryClient();

  const potentialAdditionalRelationships = useGetAdditionalRelationshipsForVerificationMethod();

  const sendSuccessNotification = useConnectToBrokersSuccessNotification();

  const handleSubmit = useCallback(({
    isTermsAcknowledged,
    payeeContactMethodId,
    payeeRelationshipIds,
    verificationCode,
  }: ClaimRelationshipsForContactMethodRequest) => {
    setIsVerifying(true);

    // Capture these before making the request to ensure closure
    const relationshipIdsBeingClaimed = filteredUnclaimedRelationships?.map(unclaimedRelationship =>
      unclaimedRelationship.id) ?? [];

    const isComingFromVerificationDialog = (claimRelationshipState.selectedPayeeRelationshipIds ?? [])
      .some(id => payeeRelationshipIds?.includes(id));

    const shouldShowClaimMoreBrokersDialog = !!potentialAdditionalRelationships.length &&
      isComingFromVerificationDialog;

    const shouldShowQuickPayDialog = !!filteredUnclaimedRelationships?.some(filterRelationshipsOfferingQuickPay);

    claimRelationshipsForContactMethod({
      isTermsAcknowledged,
      payeeContactMethodId,
      payeeRelationshipIds,
      verificationCode,
    },
    {
      onError: () => {
        setIsVerifying(false);
      },
      onSuccess: async () => {
        try {
          await refetchQuickPayableRelationships();
          await refetchRelationships();

          if (!shouldShowClaimMoreBrokersDialog) {
            sendSuccessNotification(relationshipIdsBeingClaimed.length);
          }

          await onSubmitVerificationCodeSuccess({
            claimRelationshipDispatch,
            queryClient,
            relationshipIdsBeingClaimed,
            shouldShowClaimMoreBrokersDialog,
            shouldShowQuickPayDialog,
          });
        } finally {
          setIsVerifying(false);
        }
      },
    });
  },
  [
    claimRelationshipDispatch,
    claimRelationshipState.selectedPayeeRelationshipIds,
    claimRelationshipsForContactMethod,
    filteredUnclaimedRelationships,
    potentialAdditionalRelationships.length,
    queryClient,
    refetchQuickPayableRelationships,
    refetchRelationships,
    sendSuccessNotification]);

  return useMemo(() => ({
    handleSubmit,
    isSubmitting: isVerifying,
  }),
  [handleSubmit, isVerifying]);
};

export const useSubmitWithoutVerificationCode = () => {
  // Control the submitting state ourselves.
  // We need it to remain true until the onSuccess is done, after promises are resolved.
  // We want the submit button to remain in a loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const claimRelationshipDispatch = useClaimRelationshipDispatch();
  const claimRelationshipState = useClaimRelationshipState();

  const filteredUnclaimedRelationships = useFilteredUnclaimedRelationships();

  const {
    mutate: claimRelationshipWithoutVerificationMethod,
  } = useClaimRelationshipWithoutVerification();

  const {
    refetch: refetchQuickPayableRelationships,
  } = useGetQuickPayableInvoices({ shouldIncludeRelationshipsLackingInvoices: true });

  const {
    refetch: refetchRelationships,
  } = useGetRelationships();

  const queryClient = useQueryClient();

  const handleSubmit = useCallback(({
    payeeRelationshipId,
  }: {
    payeeRelationshipId: string;
  }) => {
    const shouldShowQuickPayDialog = (filteredUnclaimedRelationships ?? [])
      .filter(rel => rel.id === payeeRelationshipId)
      .some(filterRelationshipsOfferingQuickPay);

    setIsSubmitting(true);

    claimRelationshipWithoutVerificationMethod({
      id: payeeRelationshipId,
      payload: claimRelationshipState.brokerAuthorizationFormPayload,
    },
    {
      onError: () => {
        setIsSubmitting(false);
      },
      onSuccess: async () => {
        try {
          await refetchQuickPayableRelationships();
          await refetchRelationships();

          await onSubmitVerificationCodeSuccess({
            claimRelationshipDispatch,
            queryClient,
            relationshipIdsBeingClaimed: [payeeRelationshipId],
            shouldShowClaimMoreBrokersDialog: false,
            shouldShowQuickPayDialog,
          });
        } finally {
          setIsSubmitting(false);
        }
      },
    });
  },
  [
    claimRelationshipDispatch,
    claimRelationshipState.brokerAuthorizationFormPayload,
    claimRelationshipWithoutVerificationMethod,
    filteredUnclaimedRelationships,
    queryClient,
    refetchRelationships,
    refetchQuickPayableRelationships,
  ]);

  return useMemo(() => ({
    handleSubmit,
    isSubmitting,
  }),
  [handleSubmit, isSubmitting]);
};

export const useGetDialogToShow = (): DialogType | null => {
  const claimRelationshipState = useClaimRelationshipState();

  if (claimRelationshipState.isVerificationDialogOpen) {
    return 'verificationCode';
  }

  if (claimRelationshipState.isClaimMoreBrokersDialogOpen) {
    return 'claimMoreBrokers';
  }

  if (claimRelationshipState.quickPayDialogProps.isOpen) {
    return 'quickPay';
  }

  return null;
};
